import { MouseEvent, useMemo, useState } from "react";
import { generatePath, useNavigate } from "react-router-dom";

import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Box, MenuItem, Popover, Typography } from "@mui/material";

import { RouteEnum } from "../../../../common/models/enums";
import { formatTimestamp } from "../../../../common/utils/timestampUtils";
import { useGetRecordingsByEvent } from "../../hooks/recordings/useGetRecordingsByEvent";
import { useEventLeakTableColumns } from "../../hooks/useEventLeakTableColumns";
import { EventViewModel } from "../../models/eventViewModel";
import { RecordingViewModel } from "../../models/recordingViewModel";
import { RECORDING_PAGE_SIZE } from "../../utils/constants";
import EventTable, { ExpandableData } from "./EventTable";

interface GasLeakEventTableProps {
  event: EventViewModel;
  newestRecording?: RecordingViewModel;
}

const GasLeakEventTable = ({
  event,
  newestRecording,
}: GasLeakEventTableProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const navigate = useNavigate();

  const handleMoreActionsClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const { recordings, fetchNextPage, lastEvaluatedKey } =
    useGetRecordingsByEvent({
      customerId: event.customerId.slice(2),
      eventTimestamp: event.timestamp,
      pageSize: RECORDING_PAGE_SIZE,
    });

  const open = Boolean(anchorEl);
  const id = open ? "edit-popover" : undefined;

  const { columns } = useEventLeakTableColumns(
    newestRecording,
    handleMoreActionsClick
  );

  const expandableData: ExpandableData[] = useMemo(
    () => [
      {
        label: "DEVICE ID",
        value: newestRecording?.rawEventInfo.data.camera_id ?? "",
      },
      {
        label: "ZONE ID",
        value: newestRecording?.rawEventInfo.data.zone_id ?? "",
      },
      {
        label: "CAMERA ID",
        value: newestRecording?.rawEventInfo.data.camera_id ?? "",
      },
      {
        label: "LEAK VOLUME",
        value: newestRecording?.rawEventInfo.data.leak_volume ?? "",
      },
      {
        label: "INSTANCE NAME",
        value: newestRecording?.rawEventInfo.data.instance_name ?? "",
      },
    ],
    [newestRecording]
  );

  const newestRecordingTimestamp =
    newestRecording?.timestamp && formatTimestamp(newestRecording.timestamp);

  return (
    <>
      <EventTable
        columns={columns}
        event={event}
        expandableData={expandableData}
        records={recordings}
        latestRecordingTimestamp={newestRecordingTimestamp}
        fetchNextPage={fetchNextPage}
        shouldLoadMore={!!lastEvaluatedKey}
      />

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        sx={{
          "& .MuiPaper-root": {
            borderRadius: "0.5rem",
            minWidth: "180px",
            boxShadow: "none",
            border: theme =>
              `1px solid ${theme.palette.otherOutlineBorder.main}`,
          },
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem
          onClick={() =>
            navigate(
              generatePath(RouteEnum.EditEventDetails, {
                eventId: encodeURIComponent(event.id),
              })
            )
          }
          sx={{ p: 0 }}
        >
          <Box
            sx={{
              px: "12px",
              py: "8px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <EditOutlinedIcon
              sx={{
                color: theme => theme.palette.primary.main,
                mx: "8px",
              }}
            />
            <Typography variant="body2Regular">Edit</Typography>
          </Box>
        </MenuItem>
      </Popover>
    </>
  );
};

export default GasLeakEventTable;
